import {
    ComponentSegmentRelationAnchor,
    ComponentSegmentRelationVersion,
} from 'src/models/anchor-and-version/component-segment-relation.model';

export interface ComponentSegmentRelationRow {
    anchorKey?: number;
    isActive: boolean;
    isNew: boolean;
    originalIsActive?: boolean;
    originalSegmentAnchorKey?: number;
    rowId: string;
    segmentAnchorKey: number | null;
    segmentName: string;
}

export type ExistingComponentSegmentRelationRow = ComponentSegmentRelationRow & {
    anchorKey: number;
};

export function createNewComponentSegmentRelationRow(rowId: string): ComponentSegmentRelationRow {
    return {
        isActive: true,
        isNew: true,
        rowId,
        segmentAnchorKey: null,
        segmentName: '',
    };
}

export function createRowsFromAnchors(
    anchors: ComponentSegmentRelationAnchor[]
): ComponentSegmentRelationRow[] {
    return anchors
        .map((anchor) => createRow(anchor))
        .filter((row): row is ComponentSegmentRelationRow => row !== null);
}

export function createComponentSegmentRelationAnchorPayload(
    row: ComponentSegmentRelationRow,
    componentAnchorKey: number,
    changeRequestKey: number
): Record<string, unknown> {
    return {
        logText: '',
        componentAnchorKey,
        segmentAnchorKey: row.segmentAnchorKey,
        allVersions: [createComponentSegmentRelationVersionPayload(row, 0, changeRequestKey)],
    };
}

export function createComponentSegmentRelationVersionPayload(
    row: ComponentSegmentRelationRow,
    anchorKey: number,
    changeRequestKey: number
): Record<string, unknown> {
    return {
        anchorKey,
        changeRequestKey,
        comment: '',
        isActive: Boolean(row.isActive),
        logText: '',
        name: row.segmentName || String(row.segmentAnchorKey ?? ''),
    };
}

export function getRowsToSave(rows: ComponentSegmentRelationRow[]): {
    changedRows: ExistingComponentSegmentRelationRow[];
    newRows: ComponentSegmentRelationRow[];
} {
    return {
        newRows: rows.filter((row) => row.isNew),
        changedRows: rows.filter(
            (row): row is ExistingComponentSegmentRelationRow =>
                !row.isNew && typeof row.anchorKey === 'number' && hasRowChanged(row)
        ),
    };
}

export function getComponentSegmentRelationValidationMessage(
    rows: ComponentSegmentRelationRow[]
): string | undefined {
    if (hasEmptySegment(rows.filter((row) => row.isNew))) {
        return 'All segment relation rows must have a segment selected';
    }

    if (hasDuplicateSegments(rows)) {
        return 'Each segment can only be added once';
    }

    return undefined;
}

function createRow(anchor: ComponentSegmentRelationAnchor): ComponentSegmentRelationRow | null {
    const latestVersion = anchor.validVersion ?? getNewestVersion(anchor.allVersions ?? []);
    if (!latestVersion) return null;

    return {
        anchorKey: anchor.key,
        isActive: Boolean(latestVersion.isActive),
        isNew: false,
        originalIsActive: Boolean(latestVersion.isActive),
        originalSegmentAnchorKey: anchor.segmentAnchorKey,
        rowId: `existing-${anchor.key}`,
        segmentAnchorKey: anchor.segmentAnchorKey,
        segmentName: '',
    };
}

function getNewestVersion(
    versions: ComponentSegmentRelationVersion[]
): ComponentSegmentRelationVersion | undefined {
    return [...versions].sort((a, b) => getVersionSortValue(b) - getVersionSortValue(a))[0];
}

function getVersionSortValue(version: ComponentSegmentRelationVersion): number {
    const timestamp = version.createdTimestamp ? new Date(version.createdTimestamp).getTime() : 0;
    return timestamp || version.key || 0;
}

function hasEmptySegment(rows: ComponentSegmentRelationRow[]): boolean {
    return rows.some((row) => row.segmentAnchorKey === null || row.segmentAnchorKey === undefined);
}

function hasDuplicateSegments(rows: ComponentSegmentRelationRow[]): boolean {
    // Existing segment selections are read-only; only new rows can introduce duplicates.
    const keys = new Set(rows.filter((row) => !row.isNew).map((row) => row.segmentAnchorKey));
    return rows
        .filter((row) => row.isNew)
        .some((row) => {
            if (row.segmentAnchorKey === null || row.segmentAnchorKey === undefined) return false;
            if (keys.has(row.segmentAnchorKey)) return true;
            keys.add(row.segmentAnchorKey);
            return false;
        });
}

function hasRowChanged(row: ComponentSegmentRelationRow): boolean {
    return row.isActive !== row.originalIsActive;
}
